import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import {
  DiscountCreateResponse,
  PageDiscountResponse,
  SegmentDiscountResponse,
} from '@app/api/marketplace/models';
import { DiscountsService } from '@app/api/marketplace/services';
import { AuthenticationSelectors } from '@authentication/store/authentication.slice';

import { SegmentDiscountsActions } from './segment-discounts.slice';

@Injectable({ providedIn: 'root' })
export class SegmentDiscountsEffects {
  loadSegmentDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SegmentDiscountsActions.loadSegmentDiscounts.trigger),
      switchMap((action) => {
        return this.discountService
          .getDiscountsList({ discountType: 'SGM', page: 0, size: 9999 })
          .pipe(
            map((response: PageDiscountResponse) =>
              SegmentDiscountsActions.loadSegmentDiscounts.success({
                response: response.content as Array<SegmentDiscountResponse>,
              })
            ),
            catchError(() =>
              of(SegmentDiscountsActions.loadSegmentDiscounts.failure())
            )
          );
      })
    )
  );

  createSegmentDiscount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SegmentDiscountsActions.createSegmentDiscount.trigger),
      withLatestFrom(this.store.select(AuthenticationSelectors.selectUserInfo)),
      switchMap(([action, userInfo]) => {
        return this.discountService
          .createDiscount({
            body: {
              ...action.discount,
              fromSiteId: userInfo.attributes?.['custom:selectedSiteId'],
            },
          })
          .pipe(
            map((response: DiscountCreateResponse) =>
              SegmentDiscountsActions.createSegmentDiscount.success({
                discount: response as SegmentDiscountResponse,
              })
            ),
            catchError(() =>
              of(SegmentDiscountsActions.createSegmentDiscount.failure())
            )
          );
      })
    )
  );

  updateSegmentDiscount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SegmentDiscountsActions.updateSegmentDiscount.trigger),
      switchMap((action) => {
        return this.discountService
          .updateDiscount({
            discountId: action.discount.id!,
            body: action.discount,
          })
          .pipe(
            map(() =>
              SegmentDiscountsActions.createSegmentDiscount.success({
                discount: action.discount as SegmentDiscountResponse,
              })
            ),
            catchError(() =>
              of(SegmentDiscountsActions.createSegmentDiscount.failure())
            )
          );
      })
    )
  );

  deleteSegmentDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SegmentDiscountsActions.deleteSegmentDiscount.trigger),
      switchMap((action) => {
        return this.discountService
          .deleteDiscounts({
            body: {
              discountIdList: action.discounts.map((d) => d.id!),
            },
          })
          .pipe(
            map(() =>
              SegmentDiscountsActions.deleteSegmentDiscount.success({
                discounts: action.discounts,
              })
            ),
            catchError(() =>
              of(SegmentDiscountsActions.deleteSegmentDiscount.failure())
            )
          );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly discountService: DiscountsService
  ) {}
}
